import React, { RefObject, useState } from 'react';

import { PopoverLayout } from './popoverPlacement';
import { SourceFavicon } from './SourceFavicon';
import {
  CitationCardBody,
  CitationCardExcerpt,
  CitationCardNav,
  CitationCardPanel,
  CitationCardTitle,
  CitationCardTitleLink,
  CitationCardTitleRow,
  CitationNavButton,
  CitationNavCounter,
} from './webSearch.styles';
import { MessageCitation } from './webSearch.types';

interface CitationCardProps {
  citations: MessageCitation[];
  layout: PopoverLayout;
  panelRef: RefObject<HTMLDivElement | null>;
}

export const CitationCard = ({ citations, layout, panelRef }: CitationCardProps) => {
  const [prevCitations, setPrevCitations] = useState(citations);
  const [index, setIndex] = useState<number>(0);

  if (citations !== prevCitations) {
    setPrevCitations(citations);
    setIndex(0);
  }

  const current = citations[index] ?? citations[0];
  if (!current) return null;

  const total = citations.length;
  const displayIndex = index + 1;
  const lastIndex: number = total - 1;

  const handlePrev = () => setIndex((i: number) => Math.max(0, i - 1));
  const handleNext = () => setIndex((i: number) => Math.min(lastIndex, i + 1));

  return (
    <CitationCardPanel
      ref={panelRef}
      $maxHeight={layout.maxHeight}
      $placement={layout.placement}
      style={{ top: layout.top, left: layout.left }}
    >
      {total > 1 && (
        <CitationCardNav>
          <CitationNavButton onClick={handlePrev} disabled={index === 0}>
            ←
          </CitationNavButton>
          <CitationNavButton onClick={handleNext} disabled={index === lastIndex}>
            →
          </CitationNavButton>
          <CitationNavCounter>{`${displayIndex}/${total}`}</CitationNavCounter>
        </CitationCardNav>
      )}
      <CitationCardBody>
        <CitationCardTitleRow>
          {current.url && <SourceFavicon url={current.url} title={current.title} />}
          {current.url ? (
            <CitationCardTitleLink href={current.url} target="_blank" rel="noopener noreferrer">
              {current.title}
            </CitationCardTitleLink>
          ) : (
            <CitationCardTitle>{current.title}</CitationCardTitle>
          )}
        </CitationCardTitleRow>
        <CitationCardExcerpt>{current.text ?? current.title}</CitationCardExcerpt>
      </CitationCardBody>
    </CitationCardPanel>
  );
};
